'use client';

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { MentionOverflowEntry } from './mentionOverflow';

export type MentionOverflowElementMap = Map<HTMLElement, MentionOverflowEntry>;

type MentionOverflowContextValue = {
  overflowByElement: MentionOverflowElementMap;
  setOverflowByElement: (map: MentionOverflowElementMap) => void;
};

const MentionOverflowContext = createContext<MentionOverflowContextValue>({
  overflowByElement: new Map(),
  setOverflowByElement: () => undefined,
});

export function MentionOverflowProvider({ children }: { children: ReactNode }) {
  const [overflowByElement, setOverflowByElement] =
    useState<MentionOverflowElementMap>(new Map());
  const value = useMemo(
    () => ({ overflowByElement, setOverflowByElement }),
    [overflowByElement],
  );

  return (
    <MentionOverflowContext.Provider value={value}>
      {children}
    </MentionOverflowContext.Provider>
  );
}

export function useMentionOverflowElements(): MentionOverflowElementMap {
  return useContext(MentionOverflowContext).overflowByElement;
}

export function useSetMentionOverflowElements(): (
  map: MentionOverflowElementMap,
) => void {
  return useContext(MentionOverflowContext).setOverflowByElement;
}
